import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Droplets, Camera, Wrench, Sparkles, Settings, Building, CheckCircle2, Phone } from "lucide-react";
import { api } from "../lib/api";
import CTASection from "../components/CTASection";
import { renderMarkdown } from "../lib/markdown";

const iconMap = { Droplets, Camera, Wrench, Sparkles, Settings, Building };

export function ServicesListPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    document.title = "Pool Services | Pool Guru Treasure Coast";
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  return (
    <div data-testid="services-page">
      <section className="bg-gradient-to-b from-pink-50/50 to-white py-20">
        <div className="section-container">
          <div className="overline mb-3">Our Services</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 max-w-4xl">
            Complete pool care, start to <span className="text-brand-pink">sparkling clean</span>.
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl">
            From weekly maintenance to emergency green-pool recovery and major repairs — we handle everything your pool needs.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = iconMap[s.icon] || Droplets;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group bg-white rounded-2xl border border-slate-200 p-8 hover:-translate-y-1 hover:shadow-xl hover:border-brand-pink/30 transition-all"
                data-testid={`services-list-${s.slug}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-pink/10 to-brand-blue/10 flex items-center justify-center mb-5 group-hover:from-brand-pink group-hover:to-brand-pink-hover transition-all">
                  <Icon className="w-6 h-6 text-brand-pink group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold mb-2">{s.name}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{s.short_description}</p>
                <span className="text-brand-blue font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <CTASection />
    </div>
  );
}

export function ServiceDetailPage() {
  const { serviceSlug, citySlug } = useParams();
  const [service, setService] = useState(null);
  const [city, setCity] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (citySlug) {
      api.get(`/combo/${serviceSlug}/${citySlug}`).then((r) => {
        setService(r.data.service);
        setCity(r.data.city);
        document.title = `${r.data.service.name} in ${r.data.city.name}, FL | Pool Guru`;
      }).catch(() => {});
    } else {
      api.get(`/services/${serviceSlug}`).then((r) => {
        setService(r.data);
        document.title = `${r.data.name} | Pool Guru Treasure Coast`;
      });
    }
    api.get("/cities").then((r) => setCities(r.data.filter((c) => c.is_featured)));
  }, [serviceSlug, citySlug]);

  if (!service) return <div className="section-container py-20 text-center">Loading...</div>;

  const Icon = iconMap[service.icon] || Droplets;
  const locationLabel = city ? ` in ${city.name}, FL` : "";

  return (
    <div data-testid={`service-detail-${serviceSlug}`}>
      <section className="bg-gradient-to-b from-pink-50/50 to-white py-20">
        <div className="section-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-pink to-brand-pink-hover flex items-center justify-center shadow-lg shadow-pink-500/20">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="overline">{city ? `${city.name}, FL` : "Service"}</div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              {service.name}
              {city && <span className="block text-brand-pink mt-2">in {city.name}, FL</span>}
            </h1>
            <p className="text-xl text-slate-600 mb-8">{service.short_description}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary" data-testid="service-cta-estimate">
                Get Free Estimate <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:7722890405" className="btn-secondary" data-testid="service-cta-phone">
                <Phone className="w-4 h-4" /> 772-289-0405
              </a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img src={service.image} alt={service.name} className="w-full h-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 prose-content">
            {city && (
              <p className="text-lg text-slate-700 font-medium mb-6">
                Pool Guru Treasure Coast proudly provides {service.name.toLowerCase()}{locationLabel}.
                As a family-owned company with over 20 years of experience across the Treasure Coast,
                we bring the same detailed, reliable service to every pool we maintain.
              </p>
            )}
            <div className="text-slate-700 leading-relaxed">{renderMarkdown(service.content)}</div>
            {city && (
              <div className="mt-10 p-8 bg-gradient-to-br from-pink-50 to-blue-50 rounded-2xl border border-pink-100">
                <h3 className="text-2xl font-bold mb-3">
                  {service.name} in {city.name}, FL
                </h3>
                <p className="text-slate-700 mb-4">
                  Homeowners searching for "{service.name.toLowerCase()} near me in {city.name}" trust
                  Pool Guru for reliable weekly service, fair pricing, and the detailed reporting that
                  gives them full confidence in their pool's condition.
                </p>
                <Link to={`/pool-service-${city.slug}`} className="btn-ghost" data-testid="service-visit-city">
                  See all services in {city.name} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div className="bg-brand-pink rounded-2xl p-7 text-white shadow-xl shadow-pink-500/20">
              <div className="overline text-white/90 mb-3">Request service</div>
              <div className="font-heading font-extrabold text-2xl mb-2">772-289-0405</div>
              <p className="text-white/90 text-sm mb-4">
                Free estimates for {service.name.toLowerCase()}{locationLabel}.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-brand-pink font-bold rounded-lg px-5 py-2.5 hover:bg-slate-100">
                Get Free Estimate <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-7">
              <div className="overline mb-3">Available in</div>
              <ul className="space-y-2">
                {cities.slice(0, 8).map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/services/${service.slug}/${c.slug}`}
                      className="text-sm font-semibold text-slate-700 hover:text-brand-pink flex items-center justify-between group"
                      data-testid={`combo-link-${service.slug}-${c.slug}`}
                    >
                      <span>
                        <CheckCircle2 className="w-3.5 h-3.5 inline mr-2 text-brand-pink" />
                        {service.name} {c.name}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <CTASection locationName={city?.name} />
    </div>
  );
}
